import { createAsync, query } from "@solidjs/router";
import { For, Show } from "solid-js";

// Income sits on the dashboard only. The monthly scheme report goes to a wide
// audience and deliberately carries no money; a separate, smaller-circulation
// income email can call getIncome() with a calendar-month window.
const getIncomeSummary = query( async () => {
	"use server";

	const { getIncome } = await import( "~/_server/income" );

	const now = Math.floor( Date.now() / 1000 );
	const DAY = 24 * 60 * 60;

	// Fixed cache keys - the window drifting a second between page views must not
	// force a fresh Xero call.
	const [current, prior] = await Promise.all( [
		getIncome( now - 30 * DAY, now, "rolling30" ),
		getIncome( now - 60 * DAY, now - 30 * DAY, "rolling30-prior" ),
	] );

	return { current, prior };
}, "getIncomeSummary" );

const plural = ( n: number, one: string, many = `${one}s` ) => `${n} ${n === 1 ? one : many}`;

const money = ( n: number ) =>
	n >= 1_000_000 ? `£${( n / 1_000_000 ).toFixed( 1 )}m`
	: n >= 10_000 ? `£${( n / 1_000 ).toFixed( 1 )}k`
	: `£${Math.round( n ).toLocaleString( "en-GB" )}`;

export const IncomeSummary = () => {
	const data = createAsync( () => getIncomeSummary() );

	// One colour per source, driving the bar segment, the card edge and the figure
	// from a single value so the bar reads as a legend for the cards below it.
	//
	// Applied as the theme variable rather than through border-l-* utilities:
	// those compete with border-gray-200 on the same element and the winner comes
	// down to the order Tailwind happens to emit them in, which left some cards
	// with a grey edge and others coloured.
	const rows = () => {
		const d = data();

		if ( !d ) return [];

		return [
			{ label: "Joining", now: d.current.joining, prev: d.prior.joining, colour: "var(--color-primary)" },
			{ label: "Renewals", now: d.current.renewal, prev: d.prior.renewal, colour: "var(--color-sky-700)" },
			{ label: "Certificates", now: d.current.certificates, prev: d.prior.certificates, colour: "var(--color-accent)" },
			...( d.current.other || d.prior.other
				? [{ label: "Other", now: d.current.other, prev: d.prior.other, colour: "var(--color-gray-400)" }]
				: [] ),
		];
	};

	const split = () => {
		const total = data()?.current.total ?? 0;

		if ( !total ) return [];

		return rows().map( r => ( { ...r, pct: Math.round( r.now / total * 100 ) } ) ).filter( r => r.pct > 0 );
	};

	const delta = ( now: number, prev: number ) => {
		const d = now - prev;

		if ( d === 0 ) return <span class="text-[11px] text-gray-400">no change</span>;

		return (
			<>
				<span class={`text-[11px] font-bold ${d > 0 ? "text-green-600" : "text-red-600"}`}>
					{d > 0 ? "▲" : "▼"} {money( Math.abs( d ) )}
				</span>
				<span class="text-[11px] text-gray-400"> vs prior 30</span>
			</>
		);
	};

	return (
		<div class="white-box">
			<div class="flex items-baseline justify-between mb-4">
				<h2 class="text-xl text-black font-bold">Income</h2>
				<span class="text-[11px] text-gray-400">Payments banked in the past 30 days, net of VAT</span>
			</div>

			<Show when={data()} fallback={<p class="text-sm text-gray-500">Loading…</p>}>
				<Show
					when={!data()!.current.error}
					fallback={
						<p class="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
							Could not reach Xero — {data()!.current.error}
						</p>
					}>

					<Show when={split().length}>
						<div class="flex h-2 rounded overflow-hidden mb-4">
							<For each={split()}>
								{seg => <div style={{ width: `${seg.pct}%`, background: seg.colour }} title={`${seg.label} ${money( seg.now )}`} />}
							</For>
						</div>
					</Show>

					<div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
						<For each={rows()}>
							{r => (
								<div class="border border-gray-200 border-l-4 rounded-lg p-3" style={{ "border-left-color": r.colour }}>
									<p class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{r.label}</p>
									<p class="text-2xl font-bold leading-tight" style={{ color: r.colour }}>{money( r.now )}</p>
									{delta( r.now, r.prev )}
								</div>
							)}
						</For>

						<div class="border border-gray-300 bg-gray-50 rounded-lg p-3">
							<p class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</p>
							<p class="text-2xl font-bold text-gray-800 leading-tight">{money( data()!.current.total )}</p>
							{delta( data()!.current.total, data()!.prior.total )}
						</div>
					</div>

					<p class="text-[11px] text-gray-500 mt-3">
						{plural( data()!.current.paymentCount, "payment" )} received, {money( data()!.current.cash )} including VAT.
					</p>

					<Show when={data()!.current.unallocated}>
						<p class="text-[11px] text-amber-700 mt-1">
							{money( data()!.current.unallocated! )} could not be matched to an invoice, so it is counted in
							the cash figure but not split across the categories above.
						</p>
					</Show>
				</Show>
			</Show>
		</div>
	);
};
